import { UserLocation } from '@/lib/user-location';
import { isRegionPrefSource, isRegionPriority } from '@/lib/subregion-pref';

// Cross-origin location handoff: each region lives on its own apex domain in
// prod, so localStorage set on the /region page does not follow the user when
// they switch country. The chosen location rides along as a compact `loc`
// query param; LocationHandoff consumes and strips it on landing.

type CompactLocation = {
  c: string; // city
  r: string; // region code/name
  k: string; // country code
  la?: number;
  ln?: number;
  p?: string; // regionPriority — 'subregion-first' | 'country-first'
  s?: string; // regionSource   — 'explicit' | 'detected'
};

export function encodeLocationParam(loc: UserLocation): string {
  const compact: CompactLocation = {
    c: loc.city.slice(0, 80),
    r: loc.region.slice(0, 80),
    k: loc.country.slice(0, 4),
    ...(loc.coordinates
      ? {
          la: Number(loc.coordinates.lat.toFixed(5)),
          ln: Number(loc.coordinates.lng.toFixed(5)),
        }
      : {}),
    // Only ever emitted for values inside the literal unions, so a decoder that
    // trusts the key still cannot be handed junk.
    ...(isRegionPriority(loc.regionPriority) ? { p: loc.regionPriority } : {}),
    ...(isRegionPrefSource(loc.regionSource) ? { s: loc.regionSource } : {}),
  };
  return JSON.stringify(compact);
}

export function decodeLocationParam(raw: string): UserLocation | null {
  try {
    const parsed = JSON.parse(raw) as Partial<CompactLocation>;
    if (
      typeof parsed.c !== 'string' ||
      typeof parsed.r !== 'string' ||
      typeof parsed.k !== 'string' ||
      !parsed.c.trim() ||
      !parsed.k.trim() ||
      parsed.k.length > 4
    ) {
      return null;
    }
    const hasCoords = typeof parsed.la === 'number' && typeof parsed.ln === 'number';
    return {
      city: parsed.c.slice(0, 80),
      region: (parsed.r || 'All regions').slice(0, 80),
      country: parsed.k.toUpperCase(),
      ...(hasCoords ? { coordinates: { lat: parsed.la as number, lng: parsed.ln as number } } : {}),
      // TOLERANT: a link minted before these keys existed, or one whose keys
      // were hand-edited to nonsense, decodes to a location WITHOUT them — the
      // same object the old decoder produced. Absent ⇒ country-first.
      ...(isRegionPriority(parsed.p) ? { regionPriority: parsed.p } : {}),
      ...(isRegionPrefSource(parsed.s) ? { regionSource: parsed.s } : {}),
      updatedAt: Date.now(),
    };
  } catch {
    return null;
  }
}
